import { Box, Paper, Tab, Tabs, Typography } from "@mui/material"
import { FC, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import { pathConstants } from "../../pathConstants"
import { BagTypesTab } from "./BagTypesTab"
import { LockersTab } from "./LockersTab"
import { ManifestTab } from "./ManifestTab"
import { NotificationsTab } from "./NotificationsTab"
import { PickToLightTab } from "./PickToLightTab"
import { ProviderProfileTab } from "./ProviderProfileTab"
import { SystemSettingsTab } from "./SystemSettingsTab"
import { TagsTab } from "./TagsTab"
import { WorkingHoursTab } from "./WorkingHoursTab"

const tabs = [
  {
    label: "configuration.tabs.systemSettings",
    path: pathConstants.configurationSettings,
  },
  {
    label: "configuration.tabs.provider",
    path: pathConstants.configurationProvider,
  },
  { label: "configuration.tabs.tags", path: pathConstants.configurationTags },
  {
    label: "configuration.tabs.lockers",
    path: pathConstants.configurationLockers,
  },
  {
    label: "configuration.tabs.pickToLight",
    path: pathConstants.configurationPickToLight,
  },
  {
    label: "configuration.tabs.manifest",
    path: pathConstants.configurationManifest,
  },
  {
    label: "configuration.tabs.notifications",
    path: pathConstants.configurationNotifications,
  },
  {
    label: "configuration.tabs.workingHours",
    path: pathConstants.configurationWorkingHours,
  },
  {
    label: "configuration.tabs.bagTypes",
    path: pathConstants.configurationBagTypes,
  },
]

export const ConfigurationPage: FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const activeIndex = tabs.findIndex((tab) =>
    location.pathname.startsWith(tab.path),
  )

  useEffect(() => {
    if (
      location.pathname === pathConstants.configuration ||
      location.pathname === pathConstants.configuration + "/"
    ) {
      navigate(pathConstants.configurationSettings, { replace: true })
    }
  }, [location.pathname, navigate])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(tabs[newValue].path)
  }

  const renderTabContent = () => {
    const currentPath = location.pathname
    if (currentPath.startsWith(pathConstants.configurationProvider))
      return <ProviderProfileTab />
    if (currentPath.startsWith(pathConstants.configurationTags))
      return <TagsTab />
    if (currentPath.startsWith(pathConstants.configurationLockers))
      return <LockersTab />
    if (currentPath.startsWith(pathConstants.configurationPickToLight))
      return <PickToLightTab />
    if (currentPath.startsWith(pathConstants.configurationManifest))
      return <ManifestTab />
    if (currentPath.startsWith(pathConstants.configurationNotifications))
      return <NotificationsTab />
    if (currentPath.startsWith(pathConstants.configurationWorkingHours))
      return <WorkingHoursTab />
    if (currentPath.startsWith(pathConstants.configurationBagTypes))
      return <BagTypesTab />
    return <SystemSettingsTab />
  }

  return (
    <Box
      data-testid="configuration-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "auto",
        bgcolor: "grey.50",
      }}
    >
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 2 }}>
        <Typography variant="h4" fontWeight={700} color="text.primary">
          {t("configuration.title", "Configuration")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
          {t(
            "configuration.subtitle",
            "Manage your system settings and preferences",
          )}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              data-testid="configuration-tabs"
              value={activeIndex === -1 ? 0 : activeIndex}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  minHeight: 64,
                  fontWeight: 500,
                  fontSize: "0.95rem",
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.path}
                  data-testid={`configuration-tab-${tab.path}`}
                  label={t(tab.label)}
                  sx={{ textTransform: "none" }}
                />
              ))}
            </Tabs>
          </Box>
          <Box sx={{ p: 0 }}>{renderTabContent()}</Box>
        </Paper>
      </Box>
    </Box>
  )
}
